/**
 * @file LanguageSelectorView — presentation for the one language picker.
 * @description Renders the (optional) trigger plus the option surface. Two
 * presentations, chosen by SCREEN WIDTH in the container and passed in as
 * `isSmallScreen`: a bottom `Actionsheet` on small screens, a centered `Modal`
 * dialog on large ones. Both hosts are Gluestack overlay-portal families, so
 * this surface nests safely inside an already-open modal (the scout report).
 *
 * Renders through `@/components/atoms` only — the three pickers this replaces
 * between them carried four raw-RN-primitive waivers, a raw `View` waiver and
 * two style-escape-hatch waivers, and none of them survive here.
 *
 * Rows arrive pre-built from the container, each carrying its own bound
 * `onPress`, so the map below allocates no handler per render and the file
 * still holds exactly one component.
 * @module components/molecules/LanguageSelector/LanguageSelectorView
 */
import { CheckIcon, LanguagesIcon } from "lucide-react-native";
import { memo } from "react";
import { Platform } from "react-native";

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
} from "@/components/atoms/Actionsheet";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Modal, ModalBackdrop, ModalContent } from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_LANGUAGE_SELECTOR } from "e2e/testId";

/**
 * Which trigger the molecule renders.
 *
 * `"language-label"` — the icon + current-language pill (the AI summary).
 * `"none"` — no trigger at all; the surface is opened by the caller's own
 * affordance and driven through `isOpen` (the scout report's Translate pill).
 */
export type LanguageSelectorTrigger = "language-label" | "none";

/** Closed size enum for the trigger. */
export type LanguageSelectorSize = "sm" | "md";

/**
 * The states the optional save-as-default row can be in (SE-6511).
 *
 * `saved` and `already-default` are separate members because they are separate
 * claims: one says "this surface just wrote your profile", the other says "this
 * already was your default". Collapsing them would let a surface that has
 * written nothing render a confirmation.
 */
export type LanguageSelectorSaveDefaultStatus =
  | "idle"
  | "saving"
  | "saved"
  | "already-default"
  | "error";

/** The optional save-as-default action, as the molecule receives it. */
export interface LanguageSelectorSaveDefault {
  /** Which of the five states the row is in. */
  readonly status: LanguageSelectorSaveDefaultStatus;
  /** Runs the profile write. Only called from the pressable states. */
  readonly onPress: () => void;
  /** The failure message, rendered only in the `error` state. */
  readonly errorMessage?: string | null;
}

/**
 * The same row as the VIEW receives it — copy already resolved from the
 * catalog by the container (SE-6512).
 *
 * The view takes resolved strings rather than calling `t` itself so it stays
 * the pure presentation half of the pair, and so the container is the single
 * place that decides how the error state's visible label and its accessible
 * name relate.
 */
export interface LanguageSelectorSaveDefaultRow extends LanguageSelectorSaveDefault {
  /** The visible row copy for the current status. */
  readonly label: string;
  /** The accessible name, which folds in the failure reason when present. */
  readonly accessibleName: string;
  /**
   * What declining this offer costs, resolved when the offer is live and
   * absent otherwise (SE-6506).
   *
   * The product already behaved this way: a language picked here applies to
   * one summary, and unless it becomes the account default the next visit
   * generates in the default language again. It simply never SAID so, and a
   * deliberate behaviour nobody is told about is indistinguishable from a bug
   * — which is how it was reported. This is the telling.
   */
  readonly warning?: string;
}

/**
 * Catalog KEY per state — the copy itself lives in `locales/*.json` under
 * `language.saveDefault.*` (SE-6512).
 *
 * Owned here, and EXPORTED, so the two pickers that carry this affordance
 * render the same words: the AI summary is reachable through both this sheet
 * and the "Generate · {language}" dropdown, and a save row that said different
 * things in each would be two features wearing one name. Sharing the KEY rather
 * than the resolved string keeps that property across all six locales — one
 * catalog entry, both surfaces — instead of only in English.
 */
export const SAVE_DEFAULT_LANGUAGE_KEYS: Readonly<
  Record<LanguageSelectorSaveDefaultStatus, string>
> = {
  idle: "language.saveDefault.idle",
  saving: "language.saveDefault.saving",
  saved: "language.saveDefault.saved",
  "already-default": "language.saveDefault.alreadyDefault",
  error: "language.saveDefault.error",
};

/**
 * Catalog key for the consequence copy shown beside a LIVE offer (SE-6506).
 *
 * Deliberately one key rather than one per state: the consequence is a property
 * of having an unsaved non-default language, not of the row's status, and the
 * states that still carry that (`idle`, `error`) are exactly the pressable
 * ones.
 */
export const SAVE_DEFAULT_LANGUAGE_WARNING_KEY = "language.saveDefault.warning";

/**
 * Whether the row accepts a press. `saving` is inert so a second write cannot
 * be queued behind the first; `saved` and `already-default` are inert because
 * there is nothing left to write.
 * @param status - The row's state
 * @returns Whether pressing it does anything
 */
export const isSaveDefaultPressable = (
  status: LanguageSelectorSaveDefaultStatus
): boolean => status === "idle" || status === "error";

/** One rendered locale row, with its handler already bound. */
export interface LanguageSelectorRow {
  /** The locale code this row selects — also its test identity. */
  readonly value: string;
  /** The display label. */
  readonly label: string;
  /**
   * Whether this row is the current selection.
   *
   * At most one row is ever marked, and NO row is marked when the stored
   * locale sits outside the offered six or has not resolved — in neither case
   * has the user picked one of these rows, and marking one would say they had.
   */
  readonly isSelected: boolean;
  /** Stable press handler supplied by the container. */
  readonly onPress: () => void;
}

/** Props for LanguageSelectorView. */
export interface LanguageSelectorViewProps {
  /** The offered locales as rows, in canonical order. */
  readonly rows: readonly LanguageSelectorRow[];
  /** Visible trigger copy (never a raw locale code). */
  readonly triggerLabel: string;
  /** Accessible name for the trigger, which states the stranded case. */
  readonly triggerAccessibilityLabel: string;
  /** Heading shown above the option list. */
  readonly title: string;
  /** Whether the option surface is open. */
  readonly isOpen: boolean;
  /** Width-derived presentation switch (sheet when true, dialog when false). */
  readonly isSmallScreen: boolean;
  /** Which trigger to render. */
  readonly trigger: LanguageSelectorTrigger;
  /** Trigger size. */
  readonly size: LanguageSelectorSize;
  /** Whether the trigger is inert. */
  readonly disabled: boolean;
  /** Opens the surface. */
  readonly onOpen: () => void;
  /** Closes the surface. */
  readonly onClose: () => void;
  /** Optional trigger testID override; defaults to the shared anchor. */
  readonly triggerTestID?: string;
  /**
   * Optional trailing action promoting the current selection to the account
   * default (SE-6511). Omitted entirely by surfaces that have no such concept,
   * e.g. the scout-report translation sheet.
   */
  readonly saveAsDefault?: LanguageSelectorSaveDefaultRow;
}

/** Pre-built a11y state objects so no row allocates one per render. */
const SELECTED_STATE = {
  on: { selected: true },
  off: { selected: false },
} as const;

/**
 * Renders one locale row. A plain render helper rather than a component, so
 * the file keeps one component (`component-structure` allows only
 * index/Container/View in a component directory) — the shape
 * `DesktopDropdownMenuView` uses for its rows.
 * @param row - The row to render, with its handler already bound
 * @returns The row element
 */
const renderRow = (row: LanguageSelectorRow) => (
  <Pressable
    key={row.value}
    testID={TEST_ID_LANGUAGE_SELECTOR.optionFor(row.value)}
    onPress={row.onPress}
    accessibilityRole="menuitem"
    accessibilityState={SELECTED_STATE[row.isSelected ? "on" : "off"]}
    className={`px-4 py-3 active:bg-surface-subtle ${
      row.isSelected ? "bg-status-info-surface" : ""
    }`}
  >
    <Stack
      direction="horizontal"
      className="items-center justify-between gap-2"
    >
      <Text
        variant="title-sm"
        className={
          row.isSelected ? "text-accent-primary" : "text-content-primary"
        }
      >
        {row.label}
      </Text>
      {!!row.isSelected && (
        <Icon as={CheckIcon} size="sm" className="text-accent-primary" />
      )}
    </Stack>
  </Pressable>
);

/**
 * Renders the optional save-as-default row, OUTSIDE the scroll area so it stays
 * put while a long locale list scrolls — the same two-groups-in-one-surface
 * shape `SelectDropdown`'s pinned trailing group uses. It is a different KIND
 * of choice from the locale rows, so it is separated by a divider rather than
 * reading as a seventh language.
 *
 * Pressing it never closes the surface: the confirmation (or the failure) is
 * the point, and a sheet that dismisses itself would hide both.
 * @param saveAsDefault - The action and its state
 * @returns The row element
 */
const renderSaveDefaultRow = (
  saveAsDefault: LanguageSelectorSaveDefaultRow
) => {
  const { status, onPress, errorMessage, label, accessibleName, warning } =
    saveAsDefault;
  const pressable = isSaveDefaultPressable(status);
  return (
    <Box className="border-t border-outline-subtle">
      <Pressable
        testID={TEST_ID_LANGUAGE_SELECTOR.SAVE_AS_DEFAULT}
        onPress={onPress}
        disabled={!pressable}
        accessibilityRole="button"
        accessibilityLabel={accessibleName}
        accessibilityState={{ disabled: !pressable, busy: status === "saving" }}
        className={`px-4 py-3 ${pressable ? "active:bg-surface-subtle" : "opacity-70"}`}
      >
        <Stack direction="horizontal" className="items-center gap-2">
          {status === "saved" && (
            <Icon as={CheckIcon} size="sm" className="text-status-success" />
          )}
          <Text
            variant="title-sm"
            className={
              status === "error"
                ? "text-status-error"
                : "text-content-secondary"
            }
          >
            {label}
          </Text>
        </Stack>
        {status === "error" && !!errorMessage && (
          <Text variant="caption" className="text-status-error">
            {errorMessage}
          </Text>
        )}
        {!!warning && (
          <Text variant="caption" className="text-content-muted">
            {warning}
          </Text>
        )}
      </Pressable>
    </Box>
  );
};

/**
 * Renders the heading and the scrollable option list shared by both
 * presentations.
 * @param title - Heading copy
 * @param rows - The offered locales as rows
 * @param saveAsDefault - The optional save-as-default action
 * @returns The heading plus option rows
 */
const renderOptionList = (
  title: string,
  rows: readonly LanguageSelectorRow[],
  saveAsDefault?: LanguageSelectorSaveDefaultRow
) => (
  <Stack className="w-full">
    <Box className="border-b border-outline-subtle px-4 py-3">
      <Text variant="title" className="text-content-primary">
        {title}
      </Text>
    </Box>
    <ScrollView className="max-h-96 w-full">{rows.map(renderRow)}</ScrollView>
    {!!saveAsDefault && renderSaveDefaultRow(saveAsDefault)}
  </Stack>
);

/**
 * The one language picker.
 * @param props - See {@link LanguageSelectorViewProps}
 * @param props.rows - Offered locales as rows, in canonical order
 * @param props.triggerLabel - Visible trigger copy
 * @param props.triggerAccessibilityLabel - Accessible trigger name
 * @param props.title - Heading above the option list
 * @param props.isOpen - Whether the surface is open
 * @param props.isSmallScreen - Width-derived presentation switch
 * @param props.trigger - Which trigger to render
 * @param props.size - Trigger size
 * @param props.disabled - Whether the trigger is inert
 * @param props.onOpen - Opens the surface
 * @param props.onClose - Closes the surface
 * @param props.triggerTestID - Optional trigger testID override
 * @param props.saveAsDefault - Optional save-as-default action and its state
 * @returns The trigger and its option surface
 */
const LanguageSelectorView = ({
  rows,
  triggerLabel,
  triggerAccessibilityLabel,
  title,
  isOpen,
  isSmallScreen,
  trigger,
  size,
  disabled,
  onOpen,
  onClose,
  triggerTestID,
  saveAsDefault,
}: LanguageSelectorViewProps) => (
  <>
    {trigger === "language-label" && (
      <Pressable
        testID={triggerTestID ?? TEST_ID_LANGUAGE_SELECTOR.TRIGGER}
        onPress={onOpen}
        disabled={disabled}
        accessible
        accessibilityRole="button"
        accessibilityLabel={triggerAccessibilityLabel}
        className={`flex-row items-center gap-1 rounded-sm border border-outline-subtle bg-surface-raised px-2 py-1 ${
          disabled ? "opacity-50" : ""
        }`}
      >
        <Icon
          as={LanguagesIcon}
          size={size === "sm" ? "xs" : "sm"}
          className={
            disabled ? "text-content-disabled" : "text-content-secondary"
          }
        />
        <Text
          variant="caption"
          className={
            disabled ? "text-content-disabled" : "text-content-secondary"
          }
        >
          {triggerLabel}
        </Text>
      </Pressable>
    )}

    {isSmallScreen ? (
      <Actionsheet
        isOpen={isOpen}
        onClose={onClose}
        /*
         * Capability gating, not layout gating: the RN Modal host exists only
         * on native, and the web build uses the portal host instead. The
         * layout decision above is the width one. Precedent: MobileSheetChrome.
         */
        useRNModal={Platform.OS !== "web"}
      >
        <ActionsheetBackdrop />
        <ActionsheetContent
          testID={TEST_ID_LANGUAGE_SELECTOR.SHEET}
          /*
           * Horizontal padding only. The atom base already carries `pb-safe`
           * plus the Android inset hook, and overriding the bottom padding
           * here would re-open SE-4618 — the home-indicator overlap the
           * hand-rolled sheet needed a measured inset to avoid.
           */
          className="px-0"
        >
          <Box className="h-1 w-12 self-center rounded-full bg-outline-strong" />
          {renderOptionList(title, rows, saveAsDefault)}
        </ActionsheetContent>
      </Actionsheet>
    ) : (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalBackdrop />
        <ModalContent
          testID={TEST_ID_LANGUAGE_SELECTOR.DIALOG}
          className="w-80 max-w-full p-0"
        >
          {renderOptionList(title, rows, saveAsDefault)}
        </ModalContent>
      </Modal>
    )}
  </>
);

LanguageSelectorView.displayName = "LanguageSelectorView";

export default memo(LanguageSelectorView);
